import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_GAME_ID, getGameById, type GameConfig, type FeatureKey } from '@/config/games';

interface GameState {
  /** Identifiant du jeu actuellement sélectionné. */
  activeGameId: string;
  /** Change le jeu actif. */
  setActiveGame: (id: string) => void;
  /** Config complète du jeu actif. */
  getActiveGame: () => GameConfig;
  /** Le jeu actif supporte-t-il cette fonctionnalité ? */
  hasFeature: (feature: FeatureKey) => boolean;

  /**
   * Groupe de jeu choisi, PAR JEU (NationsGlory : « nova », « iris »...). Par jeu, pour qu'un
   * aller-retour par Paladium garde Iris. N'est envoyé (`X-Game-Group`) que si le jeu a des groupes
   * (cf gameGroupHeaderFor) : une valeur restée en mémoire pour un autre jeu ne part jamais.
   */
  activeGameGroupByGame: Record<string, string>;
  /** Choisit le groupe d'un jeu ; null l'oublie. */
  setActiveGameGroup: (gameId: string, key: string | null) => void;

  /**
   * Le jeu avait-il des groupes au DERNIER chargement réussi de `project-groups` ? Persisté : c'est
   * ce qui permet, au chargement suivant, de n'attendre les groupes QUE pour un jeu qui en a
   * (Paladium n'attend rien), et d'envoyer le groupe persisté quand `project-groups` échoue.
   */
  gameGroupsKnown: Record<string, boolean>;
  setGameGroupsKnown: (gameId: string, known: boolean) => void;
}

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      activeGameId: DEFAULT_GAME_ID,

      setActiveGame: (id) => set({ activeGameId: id }),

      getActiveGame: () => getGameById(get().activeGameId),

      hasFeature: (feature) => getGameById(get().activeGameId).features.includes(feature),

      activeGameGroupByGame: {},

      setActiveGameGroup: (gameId, key) => set((s) => {
        const current = s.activeGameGroupByGame[gameId] ?? null;
        if (current === key) return s;
        const next = { ...s.activeGameGroupByGame };
        if (key) next[gameId] = key; else delete next[gameId];
        return { activeGameGroupByGame: next };
      }),

      gameGroupsKnown: {},

      setGameGroupsKnown: (gameId, known) => set((s) => {
        if (!!s.gameGroupsKnown[gameId] === known) return s;
        const next = { ...s.gameGroupsKnown };
        if (known) next[gameId] = true; else delete next[gameId];
        return { gameGroupsKnown: next };
      }),
    }),
    {
      name: 'recubebe-game',
      partialize: (state) => ({
        activeGameId: state.activeGameId,
        activeGameGroupByGame: state.activeGameGroupByGame,
        gameGroupsKnown: state.gameGroupsKnown,
      }),
      // Anciens états persistés (activeGameId seul) : les deux cartes manquent, on part de {}.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<GameState>;
        return {
          ...current,
          ...p,
          activeGameGroupByGame: isStringMap(p.activeGameGroupByGame) ? p.activeGameGroupByGame : {},
          gameGroupsKnown: isBoolMap(p.gameGroupsKnown) ? p.gameGroupsKnown : {},
        };
      },
    }
  )
);

function isStringMap(v: unknown): v is Record<string, string> {
  return !!v && typeof v === 'object' && !Array.isArray(v)
    && Object.values(v as Record<string, unknown>).every((x) => typeof x === 'string');
}

function isBoolMap(v: unknown): v is Record<string, boolean> {
  return !!v && typeof v === 'object' && !Array.isArray(v)
    && Object.values(v as Record<string, unknown>).every((x) => typeof x === 'boolean');
}
